/**
 * SessionChatPanel
 *
 * A slide-in sidebar panel providing a persistent text-chat log during a
 * prioritization session. Powered by `sessionMessagesStore` (polled, DB-backed).
 *
 * Responsibilities:
 * - Display chronological message list with sender, time, and body
 * - Auto-scroll to the latest message when new ones arrive
 * - Accept user input and dispatch via the store
 * - Show a "Live" badge when the session timing mode is 'live'
 */

import { useEffect, useRef, useCallback, useState, KeyboardEvent } from 'react';
import { X, MessageSquare, Send, Wifi } from 'lucide-react';
import { useSessionMessagesStore } from '../../../stores/prioritization/sessionMessagesStore';
import type { SessionMessage } from '../../../types/prioritization/session';

export interface SessionChatPanelProps {
  sessionId: string;
  sessionName: string;
  workspaceId: string;
  currentUserId: string;
  currentUserName: string;
  isLive?: boolean;
  onClose: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('');
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 60 * 24) return formatTime(date);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Deterministic hue from a user id for avatar color variety
function avatarHue(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface MessageBubbleProps {
  message: SessionMessage;
  isSelf: boolean;
}

function MessageBubble({ message, isSelf }: MessageBubbleProps) {
  const initials = getInitials(message.userName || 'U');
  const color = avatarHue(message.userId);

  return (
    <div
      className={`flex gap-2 items-start ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}
      aria-label={`Message from ${message.userName}`}
    >
      {/* Avatar */}
      <div
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold select-none"
        style={{ backgroundColor: color }}
        title={message.userName}
      >
        {initials}
      </div>

      {/* Bubble */}
      <div className={`max-w-[75%] flex flex-col gap-0.5 ${isSelf ? 'items-end' : 'items-start'}`}>
        <div className="flex items-baseline gap-1.5">
          <span className="text-xs font-medium text-[var(--text-sec)] truncate max-w-[140px]">
            {isSelf ? 'You' : message.userName}
          </span>
          <span className="text-xs text-[var(--text-ter)] flex-shrink-0">
            {formatRelativeDate(message.createdAt)}
          </span>
        </div>
        <div
          className={` px-3 py-1.5 text-sm leading-relaxed break-words whitespace-pre-wrap ${
            isSelf
              ? 'bg-[var(--slate)] text-white '
              : 'bg-[var(--hover-tint)] text-[var(--text-pri)] '
          }`}
        >
          {message.body}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SessionChatPanel({
  sessionId,
  sessionName,
  workspaceId,
  currentUserId,
  currentUserName,
  isLive = false,
  onClose,
}: SessionChatPanelProps) {
  const getMessagesForSession = useSessionMessagesStore(s => s.getMessagesForSession);
  const sendMessage = useSessionMessagesStore(s => s.sendMessage);
  const isSending = useSessionMessagesStore(s => s.isSending);
  const error = useSessionMessagesStore(s => s.error);

  const messages = getMessagesForSession(sessionId);

  const [draft, setDraft] = useState('');
  const listEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom whenever new messages arrive
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || isSending) return;
    setDraft('');
    await sendMessage({
      sessionId,
      workspaceId,
      userId: currentUserId,
      userName: currentUserName,
      body: trimmed,
    });
    // Re-focus after send
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [draft, isSending, sendMessage, sessionId, workspaceId, currentUserId, currentUserName]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div
      className="flex flex-col h-full bg-[var(--bg-raised)] border-l border-[var(--rule)]"
      style={{ width: 320 }}
      aria-label="Session chat"
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--rule)] flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare className="w-4 h-4 text-[var(--text-ter)] flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-[var(--text-pri)] truncate">
                Chat
              </span>
              {isLive && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0">
                  <Wifi className="w-2.5 h-2.5" />
                  Live
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--text-ter)] truncate" title={sessionName}>
              {sessionName}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors flex-shrink-0"
          aria-label="Close chat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ── Message list ── */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12 select-none">
            <MessageSquare className="w-8 h-8 text-[var(--text-ter)] mb-2" />
            <p className="text-sm text-[var(--text-ter)] font-medium">
              No messages yet
            </p>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              Start the conversation
            </p>
          </div>
        ) : (
          messages.map(msg => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isSelf={msg.userId === currentUserId}
            />
          ))
        )}
        <div ref={listEndRef} />
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="px-3 py-1.5 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/30 border-t border-[var(--coral)] dark:border-[var(--coral)] flex-shrink-0">
          <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] truncate" title={error}>
            {error}
          </p>
        </div>
      )}

      {/* ── Input area ── */}
      <div className="px-3 py-2 border-t border-[var(--rule)] flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message… (Enter to send)"
            rows={1}
            disabled={isSending}
            className="flex-1 resize-none border border-[var(--rule)] bg-[var(--surface)] text-sm text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-gray-500 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent disabled:opacity-50 transition-colors"
            style={{ maxHeight: 120, minHeight: 36, overflowY: 'auto' }}
            aria-label="Message input"
          />
          <button
            onClick={handleSend}
            disabled={!draft.trim() || isSending}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-[var(--slate)] hover:bg-[var(--slate)] disabled:bg-[var(--hover-tint)] dark:disabled:bg-[var(--bg-raised)] disabled:cursor-not-allowed text-white disabled:text-[var(--text-ter)] dark:disabled:text-[var(--text-ter)] transition-colors"
            aria-label="Send message"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-[var(--text-ter)]">Shift+Enter for new line</p>
          {currentUserName && (
            <p className="text-xs text-[var(--text-ter)] truncate max-w-[160px]" title={currentUserName}>
              You: <span className="font-medium text-[var(--text-ter)]">{currentUserName}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
